import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductTitleAromaRefs
 * @property {HTMLElement} title
 */

/**
 * A custom element wrapping the product title. For Toothpaste products the title includes the
 * selected Aromas value, which must change when the shopper picks a different aroma. This element
 * listens for variant update events and swaps in the freshly rendered title from the response HTML,
 * mirroring the pattern used by `product-price.js`.
 *
 * @extends {Component<ProductTitleAromaRefs>}
 */
class ProductTitleAroma extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateTitle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updateTitle);
  }

  /**
   * Replaces the title text with the version rendered for the newly selected variant.
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event.
   */
  updateTitle = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { title } = this.refs;
    if (!title) return;

    const newTitleElement = event.detail.data.html.querySelector(
      `product-title-aroma[data-product-id="${this.dataset.productId}"] [ref="title"]`
    );
    if (!newTitleElement) return;

    title.replaceWith(newTitleElement);
  };
}

if (!customElements.get('product-title-aroma')) {
  customElements.define('product-title-aroma', ProductTitleAroma);
}
